import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import ScheduleForm from '../components/ScheduleForm';
import './Schedules.css';

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const formatTime = (t) => {
  if (!t) return '--:--';
  return t.includes(':') ? t.slice(0, 5) : t;
};

const Schedules = () => {
  const queryClient = useQueryClient();
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const { data: groups, isLoading, error } = useQuery(
    'groups',
    async () => {
      const response = await api.get('/groups');
      return response.data;
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/schedules/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('groups');
        toast.success('Horario actualizado');
        setEditingSchedule(null);
      },
      onError: (err) => {
        const d = err.response?.data?.detail;
        toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : d || 'Error al actualizar');
      },
    }
  );

  const toggleMutation = useMutation(
    ({ id, is_active }) => api.put(`/schedules/${id}`, { is_active }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('groups');
        toast.success('Estado actualizado');
      },
      onError: (err) => toast.error(err.response?.data?.detail || 'Error'),
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/schedules/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('groups');
        toast.success('Horario eliminado');
      },
      onError: (err) => toast.error(err.response?.data?.detail || 'Error al eliminar'),
    }
  );

  const handleDeleteClick = (schedule, groupName) => {
    setConfirmDialog({
      isOpen: true,
      type: 'danger',
      title: 'Eliminar Horario',
      message: `¿Eliminar el horario ${formatTime(schedule.start_time)}–${formatTime(schedule.end_time)} (${DAY_NAMES[schedule.day_of_week]}) del grupo "${groupName}"?`,
      onConfirm: () => { deleteMutation.mutate(schedule.id); setConfirmDialog(null); },
      onCancel: () => setConfirmDialog(null),
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
    });
  };

  if (isLoading) return <div className="loading">Cargando horarios...</div>;

  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>❌ Error al cargar datos</h3>
          <p>{error.response?.data?.detail || error.message}</p>
          <button className="btn btn-ghost" onClick={() => queryClient.invalidateQueries('groups')}>Reintentar</button>
        </div>
      </div>
    );
  }

  const activeGroups = (groups || []).filter(g => g.schedules && g.schedules.length > 0);
  const totalSchedules = (groups || []).reduce((acc, g) => acc + (g.schedules?.length || 0), 0);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Horarios por Grupo</h2>
          <p className="schedules-subtitle">
            Los horarios se gestionan dentro de cada grupo. Para agregar o modificar franjas, edita el grupo correspondiente.
          </p>
        </div>
        <span className="schedules-total-badge">{totalSchedules} franjas en {activeGroups.length} grupos</span>
      </div>

      {activeGroups.length === 0 ? (
        <div className="schedules-empty">
          <p>No hay grupos con horarios asignados.</p>
          <p>Crea grupos y agrega sus franjas horarias desde la sección <strong>Grupos</strong>.</p>
        </div>
      ) : (
        <div className="schedules-by-day-container">
          {activeGroups.map(group => (
            <div key={group.id} className="day-block">
              <div className="day-block-header">
                <h3>
                  {group.discipline?.icon && <span className="group-icon">{group.discipline.icon}</span>}
                  {group.name}
                </h3>
                <span className="day-count">{group.schedules.length} franja{group.schedules.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="day-block-content">
                {[...group.schedules]
                  .sort((a, b) => a.day_of_week - b.day_of_week || (a.start_time || '').localeCompare(b.start_time || ''))
                  .map(schedule => (
                  <div key={schedule.id} className="schedule-item">
                    <span className="schedule-day-tag">{DAY_NAMES[schedule.day_of_week]?.slice(0, 3)}</span>
                    <span className="schedule-time">
                      {formatTime(schedule.start_time)} — {formatTime(schedule.end_time)}
                    </span>
                    <span className={`status-badge ${schedule.is_active ? 'active' : 'inactive'}`}>
                      {schedule.is_active ? 'ACTIVO' : 'INACTIVO'}
                    </span>
                    <div className="schedule-actions">
                      <button className="btn-action btn-edit" onClick={() => setEditingSchedule({ ...schedule, groupName: group.name })}>
                        Editar
                      </button>
                      <button
                        className={`btn-action ${schedule.is_active ? 'btn-deactivate' : 'btn-activate'}`}
                        onClick={() => toggleMutation.mutate({ id: schedule.id, is_active: !schedule.is_active })}
                        disabled={toggleMutation.isLoading}
                      >
                        {schedule.is_active ? 'Desactivar' : 'Activar'}
                      </button>
                      <button
                        className="btn-action btn-delete"
                        onClick={() => handleDeleteClick(schedule, group.name)}
                        disabled={deleteMutation.isLoading}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={!!editingSchedule}
        onClose={() => setEditingSchedule(null)}
        title={editingSchedule ? `Editar franja — ${editingSchedule.groupName || ''}` : ''}
        closeOnOverlayClick={false}
      >
        {editingSchedule && (
          <ScheduleForm
            initialData={editingSchedule}
            onSubmit={(data) => updateMutation.mutate({ id: editingSchedule.id, data })}
            onCancel={() => setEditingSchedule(null)}
            isLoading={updateMutation.isLoading}
          />
        )}
      </Modal>

      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          type={confirmDialog.type || 'warning'}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onClose={confirmDialog.onCancel}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
        />
      )}
    </div>
  );
};

export default Schedules;

